import { useState } from 'react';
import {
  Card,
  Button,
  Upload,
  message,
  Modal,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Spin,
  Input
} from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
  FileTextOutlined,
  EditOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';

const { Title, Text, Paragraph } = Typography;

const DataManager = ({ documentData, onDocumentDataChange }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'preview', 'online-edit'
  const [uploadedData, setUploadedData] = useState(null);
  const [jsonPreview, setJsonPreview] = useState('');
  const [editableJson, setEditableJson] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editorLoadFailed, setEditorLoadFailed] = useState(false);

  // 生成模板数据
  const generateTemplate = () => {
    const template = {
      title: {
        english: "Your English Title Here",
        chinese: "您的中文标题"
      },
      header: {
        text: "您的页头标题",
        logoUrl: ""
      },
      summary: {
        title: "文章摘要标题",
        source: "文章来源"
      },
      chineseGuide: {
        title: "中文导读",
        content: [
          "第一条导读内容",
          "第二条导读内容"
        ]
      },
      backgroundImage: "",
      paragraphs: [
        {
          english: "Your first English paragraph here. This is where you put the original English text that you want to analyze.",
          chinese: "您的第一段中文翻译。这里放置您要分析的英文原文的中文翻译。",
          words: [
            {
              name: "example",
              phonetic: "/ɪɡˈzæmpəl/",
              partOfSpeech: "n. 例子，实例",
              example: "This is an example sentence.",
              scopeWord: "CET4 / CET6",
              chExample: "这是一个例句。"
            }
          ]
        }
      ],
      phraseCollection: {
        title: "【短语积累】",
        items: [
          { content: "example phrase - 示例短语" },
          { content: "another phrase - 另一个短语" }
        ]
      },
      paragraphSummary: {
        title: "【段落大意】",
        items: [
          { content: "第一段的大意总结" },
          { content: "第二段的大意总结" }
        ]
      }
    };
    return template;
  };

  // 下载JSON模板
  const downloadTemplate = () => {
    const template = generateTemplate();
    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;

    // 生成模板文件名，使用日期标识
    const currentDate = new Date().toISOString().split('T')[0];
    link.download = `PDF内容模板-${currentDate}.json`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success(`模板下载成功！文件名：PDF内容模板-${currentDate}.json`);
  };

  // 生成优化的文件名
  const generateFileName = () => {
    // 获取中文标题
    const chineseTitle = documentData?.title?.chinese || '';

    // 处理中文标题，移除不合法的文件名字符
    const sanitizedTitle = chineseTitle
      .replace(/[<>:"/\\|?*]/g, '') // 移除Windows不支持的字符
      .replace(/\s+/g, '') // 移除空格
      .trim();

    // 生成日期格式 YYYY-MM-DD
    const currentDate = new Date().toISOString().split('T')[0];

    // 如果中文标题为空或处理后为空，使用默认名称
    const titlePrefix = sanitizedTitle || 'PDF文档';

    // 生成最终文件名
    return `${titlePrefix}-${currentDate}.json`;
  };

  // 下载当前数据
  const downloadCurrentData = () => {
    const dataStr = JSON.stringify(documentData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generateFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // 显示包含文件名的成功消息
    const fileName = generateFileName();
    message.success(`数据导出成功！文件名：${fileName}`);
  };

  // 处理文件上传
  const handleFileUpload = (info) => {
    const file = info.file.originFileObj || info.file;
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        setUploadedData(jsonData);
        setJsonPreview(JSON.stringify(jsonData, null, 2));
        setModalType('preview');
        setIsModalVisible(true);
      } catch (error) {
        message.error('JSON文件格式错误，请检查文件内容');
        console.error('JSON解析错误:', error);
      }
    };

    reader.onerror = () => {
      message.error('文件读取失败，请重试');
    };

    reader.readAsText(file);
  };

  // 打开在线编辑
  const openOnlineEdit = () => {
    try {
      // 检查documentData是否存在
      if (!documentData) {
        message.error('没有可编辑的数据');
        return;
      }

      // 检查onDocumentDataChange函数是否存在
      if (typeof onDocumentDataChange !== 'function') {
        message.error('数据更新函数不可用');
        console.error('onDocumentDataChange is not a function');
        return;
      }

      const currentDataJson = JSON.stringify(documentData, null, 2);
      setEditableJson(currentDataJson);
      setModalType('online-edit');
      setIsModalVisible(true);
      setIsFullscreen(false); // 重置全屏状态
      setEditorLoadFailed(false); // 重置编辑器错误状态

      console.log('打开JSON编辑器成功');
    } catch (error) {
      message.error('数据序列化失败，请检查数据格式');
      console.error('JSON序列化错误:', error);
    }
  };

  // 切换全屏模式
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 保存在线编辑的数据
  const saveOnlineEdit = () => {
    try {
      const parsedData = JSON.parse(editableJson);
      const errors = validateData(parsedData);

      if (errors.length > 0) {
        message.error(`数据验证失败: ${errors.join(', ')}`);
        return;
      }

      onDocumentDataChange(parsedData);
      setIsModalVisible(false);
      setEditableJson('');
      message.success('数据保存成功！PDF已更新');
    } catch (error) {
      message.error('JSON格式错误，请检查语法');
      console.error('JSON解析错误:', error);
    }
  };

  // 验证数据结构
  const validateData = (data) => {
    const errors = [];
    
    if (!data.title?.english) errors.push('缺少英文标题');
    if (!data.title?.chinese) errors.push('缺少中文标题');
    if (!data.header?.text) errors.push('缺少页头标题');
    if (!data.paragraphs || data.paragraphs.length === 0) errors.push('至少需要一个段落');
    
    return errors;
  };

  // 应用上传的数据
  const applyUploadedData = () => {
    if (!uploadedData) return;
    
    const errors = validateData(uploadedData);
    if (errors.length > 0) {
      message.error(`数据验证失败: ${errors.join(', ')}`);
      return;
    }
    
    onDocumentDataChange(uploadedData);
    setIsModalVisible(false);
    setUploadedData(null);
    message.success('数据导入成功！PDF已更新');
  };



  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileTextOutlined />
          <span>📊 数据管理</span>
        </div>
      }
      size="small"
    >
      {/* 主要操作按钮 */}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
              block
            >
              下载模板
            </Button>
          </Col>
          <Col span={12}>
            <Upload
              accept=".json"
              showUploadList={false}
              customRequest={({ onSuccess }) => {
                setTimeout(() => onSuccess("ok"), 0);
              }}
              onChange={handleFileUpload}
              style={{ width: '100%' }}
            >
              <Button
                icon={<UploadOutlined />}
                block
              >
                上传JSON
              </Button>
            </Upload>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Button
              icon={<EditOutlined />}
              onClick={openOnlineEdit}
              block
              type="primary"
            >
              在线编辑
            </Button>
          </Col>
          <Col span={12}>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadCurrentData}
              block
              type="dashed"
            >
              导出当前
            </Button>
          </Col>
        </Row>
      </Space>

      {/* 使用说明 */}
      <Divider />
      <div style={{ fontSize: '12px', color: '#666' }}>
        <Title level={5} style={{ fontSize: '14px', margin: '0 0 8px 0' }}>使用说明：</Title>
        <Paragraph style={{ fontSize: '12px', margin: 0 }}>
          <Text strong>1. 下载模板：</Text> 获取标准JSON格式的数据模板<br/>
          <Text strong>2. 在线编辑：</Text> 直接在浏览器中编辑当前数据源<br/>
          <Text strong>3. 上传JSON：</Text> 上传编辑后的JSON文件<br/>
          <Text strong>4. 导出备份：</Text> 随时导出当前数据，保存工作成果
        </Paragraph>
      </div>

      {/* 模态框 */}
      <Modal
        title={
          modalType === 'preview' ? '📋 JSON数据预览' :
          modalType === 'online-edit' ? (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              paddingRight: '40px' // 为关闭按钮留出空间
            }}>
              <span>✏️ 在线编辑数据源</span>
              <Button
                type="text"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleFullscreen}
                title={isFullscreen ? '退出全屏' : '全屏编辑'}
                style={{
                  marginRight: '8px',
                  zIndex: 1000,
                  position: 'relative'
                }}
              />
            </div>
          ) : '数据管理'
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setUploadedData(null);
          setEditableJson('');
          setIsFullscreen(false); // 重置全屏状态
        }}
        width={isFullscreen ? '100vw' : 1000}
        style={isFullscreen ? {
          top: 0,
          paddingBottom: 0,
          maxWidth: 'none'
        } : {}}
        styles={{
          header: {
            paddingRight: '56px' // 确保标题区域不与关闭按钮重叠
          },
          body: isFullscreen ? {
            height: 'calc(100vh - 110px)', // 减去标题栏和按钮栏高度
            padding: '16px',
            overflow: 'hidden'
          } : {}
        }}
        footer={
          modalType === 'preview' ? [
            <Button key="cancel" onClick={() => setIsModalVisible(false)}>
              取消
            </Button>,
            <Button key="apply" type="primary" onClick={applyUploadedData}>
              应用到PDF
            </Button>
          ] : modalType === 'online-edit' ? [
            <Button key="cancel" onClick={() => setIsModalVisible(false)}>
              取消
            </Button>,
            <Button key="save" type="primary" onClick={saveOnlineEdit}>
              保存修改
            </Button>
          ] : null
        }
      >
        {modalType === 'preview' && (
          <div>
            <Editor
              height={isFullscreen ? 'calc(100vh - 180px)' : '600px'}
              defaultLanguage="json"
              value={jsonPreview}
              loading={
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px'
                }}>
                  <Spin size="large" tip="加载预览中..." />
                </div>
              }
              onMount={() => console.log('预览编辑器加载完成')}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 12,
                lineNumbers: 'on',
                folding: true,
                wordWrap: 'on',
                automaticLayout: true,
                theme: 'vs-dark',
                // 性能优化选项
                renderLineHighlight: 'none',
                renderWhitespace: 'none',
                smoothScrolling: true
              }}
            />
          </div>
        )}

        {modalType === 'online-edit' && (
          <div style={{ height: isFullscreen ? 'calc(100vh - 180px)' : '600px' }}>
            {editorLoadFailed ? (
              // Fallback to TextArea if Monaco Editor fails
              <div style={{ height: '100%' }}>
                <div style={{ marginBottom: '8px', color: '#ff4d4f', fontSize: '12px' }}>
                  ⚠️ 高级编辑器加载失败，使用基础文本编辑器
                </div>
                <Input.TextArea
                  value={editableJson}
                  onChange={(e) => setEditableJson(e.target.value)}
                  style={{
                    height: 'calc(100% - 30px)',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: '12px'
                  }}
                  placeholder="请输入JSON格式的数据..."
                />
              </div>
            ) : (
              <Editor
                height="100%"
                defaultLanguage="json"
                value={editableJson}
                onChange={(value) => setEditableJson(value || '')}
                loading={
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px'
                  }}>
                    <Spin size="large" tip="加载编辑器中..." />
                  </div>
                }
                onMount={(editor, monaco) => {
                  try {
                    console.log('在线编辑器加载完成');
                    // 设置编辑器主题
                    monaco.editor.setTheme('vs-dark');

                    // 添加错误处理
                    editor.onDidChangeModelContent(() => {
                      // 内容变化时的处理（如果需要）
                    });

                    // 重置错误状态
                    setEditorLoadFailed(false);
                  } catch (error) {
                    console.error('Monaco Editor 初始化错误:', error);
                    message.error('编辑器初始化失败，切换到基础编辑器');
                    setEditorLoadFailed(true);
                  }
                }}
                beforeMount={(monaco) => {
                  try {
                    // 预加载Monaco Editor
                    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                      validate: true,
                      allowComments: false,
                      schemas: [],
                      enableSchemaRequest: false
                    });
                  } catch (error) {
                    console.error('Monaco Editor 预加载错误:', error);
                    setEditorLoadFailed(true);
                  }
                }}
                onValidate={(markers) => {
                  // 处理验证标记（语法错误等）
                  if (markers && markers.length > 0) {
                    console.log('JSON 验证标记:', markers);
                  }
                }}
                options={{
                  minimap: { enabled: isFullscreen },
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  lineNumbers: 'on',
                  folding: true,
                  wordWrap: 'on',
                  formatOnPaste: true,
                  formatOnType: true,
                  automaticLayout: true,
                  theme: 'vs-dark',
                  // 性能优化选项
                  renderLineHighlight: 'line',
                  renderWhitespace: 'selection',
                  smoothScrolling: true,
                  cursorBlinking: 'smooth'
                }}
              />
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default DataManager;
